export type CategoryKey =
  | "electrician"
  | "plumber"
  | "mason"
  | "carpenter"
  | "ac_technician"
  | "cleaner"
  | "painter"
  | "handyman"
  | "laundry"
  | "babysitter"
  | "fridge_repair"
  | "moto_driver"
  | "tailor"
  | "car_mechanic"
  | "welder"
  | "gardener"
  | "phone_repair"
  | "tutor"
  | "hairdresser"
  | "security_guard"
  | "other";

export type SubcategoryKey = string;

export type CategoryDef = {
  label: string;
  subcategories: Record<SubcategoryKey, string>;
};

export const CATEGORIES: Record<CategoryKey, CategoryDef> = {
  electrician: {
    label: "Electricien",
    subcategories: {
      residential: "Residentiel",
      commercial: "Commercial",
      industrial: "Industriel",
      solar: "Solaire",
      repair: "Reparation",
    },
  },
  plumber: {
    label: "Plombier",
    subcategories: {
      residential: "Residentiel",
      commercial: "Commercial",
      drainage: "Drainage",
      water_heater: "Chauffe-eau",
      repair: "Reparation",
    },
  },
  mason: {
    label: "Macon",
    subcategories: {
      construction: "Construction",
      renovation: "Renovation",
      tiling: "Carrelage",
      plastering: "Platrage",
      foundation: "Fondation",
    },
  },
  carpenter: {
    label: "Menuisier",
    subcategories: {
      furniture: "Meubles",
      doors_windows: "Portes et Fenetres",
      kitchen: "Cuisine",
      renovation: "Renovation",
      custom: "Sur mesure",
    },
  },
  ac_technician: {
    label: "Climatisation",
    subcategories: {
      installation: "Installation",
      repair: "Reparation",
      maintenance: "Entretien",
      commercial: "Commercial",
    },
  },
  cleaner: {
    label: "Nettoyage",
    subcategories: {
      house: "Maison",
      apartment: "Appartement",
      business: "Commerce",
      office: "Bureau",
      car: "Voiture",
    },
  },
  painter: {
    label: "Peintre",
    subcategories: {
      interior: "Interieur",
      exterior: "Exterieur",
      decorative: "Decoratif",
      waterproofing: "Impermeabilisation",
    },
  },
  handyman: {
    label: "Bricoleur",
    subcategories: {
      general: "General",
      furniture_assembly: "Montage meubles",
      hanging: "Fixations murales",
      small_repairs: "Petites reparations",
    },
  },
  laundry: {
    label: "Blanchisserie",
    subcategories: {
      wash_fold: "Lavage et pliage",
      ironing: "Repassage",
      dry_cleaning: "Nettoyage a sec",
      pickup_delivery: "Ramassage et livraison",
    },
  },
  babysitter: {
    label: "Garde d'enfants",
    subcategories: {
      infant: "Nourrisson",
      toddler: "Tout-petit",
      school_age: "Age scolaire",
      overnight: "Garde de nuit",
    },
  },
  fridge_repair: {
    label: "Reparation Frigo",
    subcategories: {
      diagnostic: "Diagnostic",
      compressor: "Compresseur",
      thermostat: "Thermostat",
      door_seal: "Joint de porte",
    },
  },
  moto_driver: {
    label: "Moto / Transport",
    subcategories: {
      delivery: "Livraison",
      passenger: "Transport passager",
    },
  },
  tailor: {
    label: "Couturier",
    subcategories: {
      clothing: "Confection",
      alteration: "Retouche",
      embroidery: "Broderie",
    },
  },
  car_mechanic: {
    label: "Mecanicien auto",
    subcategories: {
      engine: "Moteur",
      electrical: "Electricite auto",
      bodywork: "Carrosserie",
    },
  },
  welder: {
    label: "Soudeur",
    subcategories: {
      gates: "Portails",
      railings: "Balustrades",
      structural: "Charpente metallique",
    },
  },
  gardener: {
    label: "Jardinier",
    subcategories: {
      maintenance: "Entretien",
      landscaping: "Amenagement",
      tree_trimming: "Elagage",
    },
  },
  phone_repair: {
    label: "Reparation telephone",
    subcategories: {
      screen: "Ecran",
      battery: "Batterie",
      software: "Logiciel",
    },
  },
  tutor: {
    label: "Soutien scolaire",
    subcategories: {
      primary: "Primaire",
      secondary: "Secondaire",
      university: "Universitaire",
    },
  },
  hairdresser: {
    label: "Coiffeur",
    subcategories: {
      men: "Homme",
      women: "Femme",
      braiding: "Tresses",
    },
  },
  security_guard: {
    label: "Agent de securite",
    subcategories: {
      residential: "Residentiel",
      event: "Evenementiel",
      night: "Garde de nuit",
    },
  },
  other: {
    label: "Autre",
    subcategories: {
      general: "General",
    },
  },
};

export const CATEGORY_KEYS = Object.keys(CATEGORIES) as CategoryKey[];

export function getCategoryLabel(category: string): string {
  const def = CATEGORIES[category as CategoryKey];
  return def?.label ?? category;
}

export function getSubcategories(category: string): Record<SubcategoryKey, string> {
  const def = CATEGORIES[category as CategoryKey];
  return def?.subcategories ?? {};
}

export function getSubcategoryLabel(category: string, subcategory: string): string {
  const subs = getSubcategories(category);
  return subs[subcategory] ?? subcategory;
}

export function isCategoryKey(value: string): value is CategoryKey {
  return value in CATEGORIES;
}
